import { VIEWS, DIALOGS } from './chat-screen-state';
import { Sentry } from '../../entry-setup/sentry';

const CONVERSATIONS_PAGE_SIZE = 20;
// Start loading the next page this far (in px) before the list's scroll
// position actually hits bottom, so the next page is ready before the user
// runs out of rows to look at.
const CONVERSATIONS_SCROLL_THRESHOLD = 120;

export function conversationActions() {
  return {
    // ─────────────────────────────────────────
    // Conversations
    // ─────────────────────────────────────────
    async loadConversations({ append = false } = {}) {
      if (append) {
        if (this.isLoadingMoreConversations || !this.hasMoreConversations) return;
        this.isLoadingMoreConversations = true;
      } else {
        this.isLoading = true;
      }
      this.error = null;

      try {
        const response = await this.$api({
          url: '/chat/conversations',
          method: 'GET',
          query: {
            limit: CONVERSATIONS_PAGE_SIZE,
            offset: append ? this.conversations.length : 0
          }
        });
        const conversations = response.data.conversations;
        this.conversations = append ? [...this.conversations, ...conversations] : conversations;
        this.hasMoreConversations = !!response.data.has_more;
        this.seedPresence(conversations);
        this.$nextTick(() => this.fillConversationListIfNeeded());
      } catch (err) {
        if (!append) this.error = 'Failed to load conversations';
        console.error('Failed to load conversations:', err);
      } finally {
        this.isLoading = false;
        this.isLoadingMoreConversations = false;
      }
    },

    onConversationListScroll(event) {
      const el = event.target;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom > CONVERSATIONS_SCROLL_THRESHOLD) return;
      this.loadConversations({ append: true });
    },

    // A short page of rows may not overflow a tall container (e.g. the full
    // chat page's sidebar), in which case the list never becomes scrollable
    // and onConversationListScroll never fires. Keep pulling pages after each
    // successful load until the list actually overflows or is exhausted.
    fillConversationListIfNeeded() {
      if (this.conversationFilter === 'groups') return;
      const el = this.$refs.conversationList;
      if (!el || el.scrollHeight > el.clientHeight) return;
      this.loadConversations({ append: true });
    },

    openConversation(conversationId, { focusMessageId = null, source = 'unknown' } = {}) {
      // Already viewing this conversation — jump to the target message if asked
      if (this.activeConversationId === conversationId) {
        if (!focusMessageId) return;

        const loaded = this.activeConversation.messages.some((m) => m.id === focusMessageId);
        if (loaded) {
          this.highlightAndScrollToMessage(focusMessageId);
        } else {
          this.activeConversation.focusMessageId = focusMessageId;
          this.loadMessages(conversationId, false, { aroundMessageId: focusMessageId });
        }
        return;
      }

      const conversationComponent = this.getConversation(conversationId)?.conversation_type === 'group'
        ? 'group conversation'
        : 'conversation';
      this.trackChat(conversationComponent, 'open', source);

      const sourceView = this.view;

      // Close any existing conversation first
      this.closeConversation();
      this.conversationReturnView = sourceView;
      this.clearMessageHighlight();

      const subscription = this.$cable.subscribeToConversation(conversationId, {
        onMessage: (message) => {
          this.handleNewMessage(conversationId, message);
        },
        onMessageHidden: (messageId, hidden) => {
          this.handleMessageHidden(conversationId, messageId, hidden);
        },
        onMessageDeleted: (messageId) => {
          this.handleMessageDeleted(conversationId, messageId);
        },
        onMessageEdited: (message) => {
          this.handleMessageEdited(conversationId, message);
        },
        onReadReceipt: (userId, lastReadAt) => {
          this.handleReadReceipt(conversationId, userId, lastReadAt);
        },
        onReaction: (data) => {
          this.handleReaction(conversationId, data);
        }
      });

      /** @type {ConversationState} */
      this.activeConversation = {
        conversationId,
        subscription,
        messages: [],
        isLoading: true,
        hasMore: true,
        hasNewer: false,
        messagesOffset: 0,
        otherParticipantLastReadAt: null,
        focusMessageId
      };

      this.$dispatch('chat:conversation-changed', { conversationId });
      this.onConversationOpened();
      this.loadMessages(conversationId, false, { aroundMessageId: focusMessageId });
    },

    openConversationById(conversationId, { source } = {}) {
      this.isOpen = true;
      this.isMinimized = false;
      this.openConversation(conversationId, { source });
    },

    async joinGroup(conversationId, { source } = {}) {
      try {
        const response = await this.$api({
          url: `/chat/conversations/${conversationId}/join`,
          method: 'POST'
        });

        const conversation = response.data.conversation;

        // Add to conversations list if not present
        if (!this.conversations.some((c) => c.id === conversation.id)) {
          this.conversations.unshift(conversation);
        }

        // Remove from public groups list since it now appears in My Groups
        this.removeFromPublicGroups(conversation.id);

        this.trackChat('group', 'join');

        // Show joined confirmation toast
        this.groupJoined = true;
        setTimeout(() => { this.groupJoined = false; }, 4000);

        // Notify other components (e.g., search results) that user joined
        this.$dispatch('chat:joined', { conversationId: conversation.id });

        // Open the conversation
        this.isOpen = true;
        this.isMinimized = false;
        this.openConversation(conversation.id, { source });
      } catch (err) {
        console.error('Failed to join group:', err);
        this.error = 'Failed to join group';
      }
    },

    async openConversationWithUser(userId, { source } = {}) {
      if (String(userId) === String(this.currentUserId)) {
        this.openOverlay({ source });
        return;
      }

      this.isOpen = true;
      this.isMinimized = false;

      try {
        const response = await this.$api({
          url: '/chat/conversations',
          method: 'POST',
          data: { participant_ids: [userId] }
        });

        const conversation = response.data.conversation;

        if (conversation.blocked_by_me) {
          this.moveConversationToBlocked(conversation);
        } else if (!this.conversations.some((c) => c.id === conversation.id)) {
          this.conversations.unshift(conversation);
        }

        this.openConversation(conversation.id, { source });
      } catch (err) {
        console.error('Failed to open conversation:', err);
        this.error = 'Failed to start conversation';
      }
    },

    closeConversation() {
      if (!this.activeConversation) return;

      if (this.activeConversation.subscription) {
        this.activeConversation.subscription.unsubscribe();
      }

      this.cancelEditMessage();
      this.closeReactionPicker();
      this.clearMessageHighlight();
      this.activeConversation = null;
      this.onConversationClosed();
    },

    // ─────────────────────────────────────────
    // Hide / Unhide
    // ─────────────────────────────────────────
    toggleMainMenu() {
      this.dialog = this.dialog === DIALOGS.MAIN_MENU ? null : DIALOGS.MAIN_MENU;
    },

    closeMainMenu() {
      if (this.dialog === DIALOGS.MAIN_MENU) this.dialog = null;
    },

    toggleDmActionsMenu() {
      this.dialog = this.dialog === DIALOGS.DM_ACTIONS ? null : DIALOGS.DM_ACTIONS;
    },

    closeDmActionsMenu() {
      if (this.dialog === DIALOGS.DM_ACTIONS) this.dialog = null;
    },

    openHiddenMessages() {
      this.view = VIEWS.HIDDEN_MESSAGES;
      this.dialog = null;
      this.loadHiddenConversations();
    },

    closeHiddenMessages() {
      this.view = VIEWS.LIST;
    },

    openBlockedContacts() {
      this.view = VIEWS.BLOCKED_CONTACTS;
      this.dialog = null;
      this.loadBlockedConversations();
    },

    closeBlockedContacts() {
      this.view = VIEWS.LIST;
    },

    async loadBlockedConversations() {
      this.isLoadingBlocked = true;
      try {
        const response = await this.$api({
          url: '/chat/conversations',
          method: 'GET',
          query: { scope: 'blocked' }
        });
        this.blockedConversations = response.data.conversations || [];
      } catch (err) {
        console.error('Failed to load blocked conversations:', err);
        this.blockedConversations = [];
      } finally {
        this.isLoadingBlocked = false;
      }
    },

    blockedContactsCountLabel() {
      const count = this.blockedConversations.length;
      if (count === 1) {
        return window.i18n?.t('chat.blocked_contacts.count_one') || '1 Contact blocked';
      }
      return window.i18n?.t('chat.blocked_contacts.count_many', { count })
        || `${count} Contacts blocked`;
    },

    moveConversationToBlocked(conv) {
      if (!conv) return;
      conv.blocked_by_me = true;
      conv.is_blocked = true;
      this.conversations = this.conversations.filter((c) => c.id !== conv.id);
      this.hiddenConversations = this.hiddenConversations.filter((c) => c.id !== conv.id);
      if (!this.blockedConversations.some((c) => c.id === conv.id)) {
        this.blockedConversations = [conv, ...this.blockedConversations];
      }
    },

    moveConversationFromBlocked(conv) {
      if (!conv) return;
      conv.blocked_by_me = false;
      conv.is_blocked = false;
      this.blockedConversations = this.blockedConversations.filter((c) => c.id !== conv.id);
      if (conv.is_hidden) {
        if (!this.hiddenConversations.some((c) => c.id === conv.id)) {
          this.hiddenConversations = [conv, ...this.hiddenConversations];
        }
        return;
      }
      if (!this.conversations.some((c) => c.id === conv.id)) {
        this.conversations = [conv, ...this.conversations];
      }
    },

    openFavoriteMessages() {
      this.dialog = null;
      this.expandedFavoriteConversationIds = [];
      this.loadingMoreFavoriteMessageIds = [];
      this.hasMoreFavoriteConversations = false;
      this.favoriteConversations = [];

      this.trackChat('favorites', 'open list');
      this.view = VIEWS.FAVORITE_MESSAGES;
      this.onFavoriteMessagesOpened();
      this.loadFavoriteConversations();
    },

    // Hook for full-page URL sync; overlay noops.
    onFavoriteMessagesOpened() {},

    closeFavoriteMessages() {
      this.closeConversation();
      this.clearMessageHighlight();
      this.view = VIEWS.LIST;
    },

    openFavoriteMessage(conversationPayload, message) {
      this.ensureConversationFromFavorite(conversationPayload);
      this.openConversation(conversationPayload.id, { focusMessageId: message.id, source: 'favorite' });
    },

    ensureConversationFromFavorite(conversationPayload) {
      if (this.getConversation(conversationPayload.id)) return;

      this.conversations.unshift({
        id: conversationPayload.id,
        public_id: conversationPayload.public_id || conversationPayload.id,
        conversation_type: conversationPayload.conversation_type,
        name: conversationPayload.name,
        is_public: conversationPayload.is_public,
        other_participant: conversationPayload.other_participant,
        unread_count: 0,
        last_message: null,
        last_message_at: null
      });
    },

    async loadFavoriteConversations({ append = false } = {}) {
      if (append) {
        if (this.isLoadingMoreFavorites || !this.hasMoreFavoriteConversations) return;
        this.isLoadingMoreFavorites = true;
      } else {
        this.isLoadingFavorites = true;
      }

      try {
        const response = await this.$api({
          url: '/chat/favorite_messages',
          method: 'GET',
          query: {
            offset: append ? this.favoriteConversations.length : 0,
            limit: 10,
            message_limit: 4
          }
        });
        const conversations = response.data.favorite_conversations || [];
        this.favoriteConversations = append
          ? [...this.favoriteConversations, ...conversations]
          : conversations;
        this.hasMoreFavoriteConversations = !!response.data.has_more;
      } catch (err) {
        console.error('Failed to load favorite conversations:', err);
        if (!append) this.favoriteConversations = [];
        this.hasMoreFavoriteConversations = false;
      } finally {
        this.isLoadingFavorites = false;
        this.isLoadingMoreFavorites = false;
      }
    },

    async loadMoreFavoriteMessages(conversationId) {
      if (this.isLoadingMoreFavoriteMessages(conversationId)) return;

      const conversation = this.favoriteConversations
        .find((c) => c.conversation.id === conversationId);
      if (!conversation?.has_more_messages) return;

      this.loadingMoreFavoriteMessageIds = [
        ...this.loadingMoreFavoriteMessageIds,
        conversationId
      ];

      try {
        const response = await this.$api({
          url: '/chat/favorite_messages',
          method: 'GET',
          query: {
            conversation_id: conversationId,
            offset: conversation.messages.length,
            limit: 4
          }
        });
        const messages = response.data.messages || [];
        this.favoriteConversations = this.favoriteConversations.map((c) => {
          if (c.conversation.id !== conversationId) return c;
          return {
            ...c,
            messages: [...c.messages, ...messages],
            has_more_messages: !!response.data.has_more
          };
        });
      } catch (err) {
        console.error('Failed to load more favorite messages:', err);
      } finally {
        this.loadingMoreFavoriteMessageIds = this.loadingMoreFavoriteMessageIds
          .filter((id) => id !== conversationId);
      }
    },

    isFavoriteGroupExpanded(conversationId) {
      return this.expandedFavoriteConversationIds.includes(conversationId);
    },

    toggleFavoriteGroup(conversationId) {
      if (this.isFavoriteGroupExpanded(conversationId)) {
        this.expandedFavoriteConversationIds = this.expandedFavoriteConversationIds
          .filter((id) => id !== conversationId);
        return;
      }
      this.expandedFavoriteConversationIds = [
        ...this.expandedFavoriteConversationIds,
        conversationId
      ];
    },

    shouldShowFavoriteViewMore(conversation) {
      return !!conversation.has_more_messages;
    },

    isLoadingMoreFavoriteMessages(conversationId) {
      return this.loadingMoreFavoriteMessageIds.includes(conversationId);
    },

    favoriteConversationTitle(conv) {
      return conv.name || conv.other_participant?.name || 'Unknown';
    },

    favoriteCountLabel(count) {
      if (count === 1) {
        return window.i18n?.t('chat.favourite_messages.count_one') || '1 favorite message';
      }
      return window.i18n?.t('chat.favourite_messages.count_many', { count })
        || `${count} favorite messages`;
    },

    // Keep the Favourites sidebar in sync without refetching, so expanded
    // groups and pagination stay put while a thread is open beside the list.
    addMessageToFavoriteConversations(message) {
      if (!message) return;

      const conversationId = message.conversation_id || this.activeConversationId;
      const existing = this.favoriteConversations
        .find((group) => group.conversation.id === conversationId);

      if (existing) {
        if (existing.messages.some((m) => m.id === message.id)) return;

        const messages = [{ ...message, is_favorite: true }, ...existing.messages];
        const favoriteCount = existing.favorite_count + 1;
        const updated = {
          ...existing,
          favorite_count: favoriteCount,
          messages,
          has_more_messages: favoriteCount > messages.length
        };

        this.favoriteConversations = [
          updated,
          ...this.favoriteConversations.filter((group) => group.conversation.id !== conversationId)
        ];
        return;
      }

      const conv = this.getConversation(conversationId);
      if (!conv) return;

      this.favoriteConversations = [
        {
          conversation: {
            id: conv.id,
            public_id: conv.public_id || conv.id,
            conversation_type: conv.conversation_type,
            name: conv.name,
            is_public: conv.is_public,
            other_participant: conv.other_participant
          },
          favorite_count: 1,
          messages: [{ ...message, is_favorite: true }],
          has_more_messages: false
        },
        ...this.favoriteConversations
      ];
    },

    removeMessageFromFavoriteConversations(messageId) {
      const conversationId = this.activeConversationId;
      const next = this.favoriteConversations.flatMap((group) => {
        if (group.conversation.id !== conversationId) return [group];

        const favoriteCount = Math.max(0, group.favorite_count - 1);
        if (favoriteCount === 0) return [];

        const messages = group.messages.filter((m) => m.id !== messageId);
        return [{
          ...group,
          favorite_count: favoriteCount,
          messages,
          has_more_messages: favoriteCount > messages.length
        }];
      });

      this.favoriteConversations = next;

      if (!next.some((group) => group.conversation.id === conversationId)) {
        this.expandedFavoriteConversationIds = this.expandedFavoriteConversationIds
          .filter((id) => id !== conversationId);
      }
    },

    async loadHiddenConversations() {
      this.isLoadingHidden = true;
      try {
        const response = await this.$api({
          url: '/chat/conversations',
          method: 'GET',
          query: { scope: 'hidden' }
        });
        this.hiddenConversations = response.data.conversations || [];
      } catch (err) {
        console.error('Failed to load hidden conversations:', err);
        this.hiddenConversations = [];
      } finally {
        this.isLoadingHidden = false;
      }
    },

    async loadHiddenUnreadCount() {
      try {
        const response = await this.$api({
          url: '/chat/conversations/hidden_unread_count',
          method: 'GET'
        });
        this.hiddenUnreadCount = response.data.hidden_unread_count || 0;
      } catch (err) {
        console.error('Failed to load hidden unread count:', err);
      }
    },

    isConversationHidden() {
      const conv = this.getConversation(this.activeConversationId);
      return conv?.is_hidden === true;
    },

    isConversationHiddenById(convId) {
      const conv = this.getConversation(convId);
      return conv?.is_hidden === true;
    },

    handleConversationHidden(conversationId) {
      const conv = this.getConversation(conversationId);
      this.conversations = this.conversations.filter((c) => c.id !== conversationId);
      if (!conv) {
        this.loadHiddenUnreadCount();
        return;
      }

      conv.is_hidden = true;
      if (!this.hiddenConversations.some((c) => c.id === conversationId)) {
        this.hiddenConversations = [conv, ...this.hiddenConversations];
      }
      this.loadHiddenUnreadCount();
    },

    async handleConversationUnhidden(conversationId) {
      this.hiddenConversations = this.hiddenConversations.filter((c) => c.id !== conversationId);
      await this.loadConversations();
      this.loadHiddenUnreadCount();
    },

    async hideConversation(convId = null) {
      const conversationId = convId ?? this.activeConversationId;
      try {
        await this.$api({
          url: `/chat/conversations/${conversationId}/hide`,
          method: 'POST'
        });
        this.trackChat('inbox', 'hide');
        this.closeDmActionsMenu();
        this.closeGroupActionsMenu();
        this.closeListMenu();
        if (conversationId === this.activeConversationId) this.closeConversation();
        this.conversations = this.conversations.filter((c) => c.id !== conversationId);
        this.lastHiddenConversationId = conversationId;
        this.lastHiddenConversationIds = [conversationId];
        this.hideConversationSuccess = true;
        setTimeout(() => { this.hideConversationSuccess = false; }, 4000);
      } catch (err) {
        console.error('Failed to hide conversation:', err);
      }
    },

    async unhideConversation(convId = null) {
      const conversationId = convId ?? this.activeConversationId;
      try {
        await this.$api({
          url: `/chat/conversations/${conversationId}/unhide`,
          method: 'POST'
        });
        this.closeListMenu();
        await this.loadConversations();
        await this.loadHiddenConversations();
        await this.loadHiddenUnreadCount();
        this.unhideConversationSuccess = true;
        setTimeout(() => { this.unhideConversationSuccess = false; }, 4000);
      } catch (err) {
        console.error('Failed to unhide conversation:', err);
      }
    },

    async undoHideConversation() {
      clearTimeout(this._hideConversationSuccessTimer);
      this.hideConversationSuccess = false;
      const ids = this.lastHiddenConversationIds?.length
        ? this.lastHiddenConversationIds
        : (this.lastHiddenConversationId ? [this.lastHiddenConversationId] : []);
      if (ids.length === 0) return;

      const results = await Promise.allSettled(ids.map((id) => this.$api({
          url: `/chat/conversations/${id}/unhide`,
          method: 'POST'
        })));
      const failedIds = [];
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failedIds.push(ids[index]);
          Sentry.captureException(result.reason, {
            extra: {
              message: 'Failed to unhide conversation',
              conversationId: ids[index]
            }
          });
        }
      });

      this.lastHiddenConversationIds = failedIds;
      this.lastHiddenConversationId = failedIds[failedIds.length - 1] || null;
      await Promise.all([
        this.loadConversations(),
        this.loadHiddenConversations(),
        this.loadHiddenUnreadCount()
      ]);

      if (results.some((result) => result.status === 'fulfilled')) {
        this.unhideConversationSuccess = true;
        setTimeout(() => { this.unhideConversationSuccess = false; }, 4000);
      }
      this.hideConversationSuccess = failedIds.length > 0;
      if (this.hideConversationSuccess) {
        this._hideConversationSuccessTimer = setTimeout(() => {
          this.hideConversationSuccess = false;
        }, 4000);
      }
    },

    toggleListMenu(convId) {
      this.listMenuConvId = this.listMenuConvId === convId ? null : convId;
    },

    closeListMenu() {
      this.listMenuConvId = null;
    },

    // ─────────────────────────────────────────
    // Select mode (mark as read / unread, hide)
    // ─────────────────────────────────────────
    enterSelectMode() {
      this.selectMode = true;
      this.selectedConversationIds = [];
      this.closeListMenu();
    },

    exitSelectMode() {
      this.selectMode = false;
      this.selectedConversationIds = [];
    },

    toggleConversationSelected(conversationId) {
      if (this.selectedConversationIds.includes(conversationId)) {
        this.selectedConversationIds = this.selectedConversationIds.filter((id) => id !== conversationId);
      } else {
        this.selectedConversationIds.push(conversationId);
      }
    },

    isConversationSelected(conversationId) {
      return this.selectedConversationIds.includes(conversationId);
    },

    isConversationUnread(conv) {
      return !!conv && ((conv.unread_count || 0) > 0 || conv.marked_unread === true);
    },

    isActiveConversationUnread() {
      return this.isConversationUnread(this.getConversation(this.activeConversationId));
    },

    // A count badge already covers real unreads. The dot is only for a chat
    // flagged unread after every message has been seen.
    showsManualUnreadDot(conv) {
      return !!conv && conv.marked_unread === true && !(conv.unread_count > 0);
    },

    // No selection marks everything ("Read all"). A selection is "Mark as read"
    // when any chosen chat is unread, and "Mark as unread" when every chosen
    // chat is already read.
    selectReadAction() {
      if (this.selectedConversationIds.length === 0) return 'read_all';

      const anyUnread = this.selectedConversationIds.some((id) => (
        this.isConversationUnread(this.getConversation(id))
      ));
      return anyUnread ? 'mark_read' : 'mark_unread';
    },

    applySelectReadAction() {
      if (this.selectReadAction() === 'mark_unread') {
        this.markSelectedConversationsUnread();
      } else {
        this.markConversationsAsRead();
      }
    },

    // With no selection this acts as "Read all" — everything from All/Unread,
    // but only the user's group chats from the Groups tab. Otherwise it marks
    // just the selected conversations and leaves them selected so the button
    // can flip to "Mark as unread".
    async markConversationsAsRead() {
      const readAll = this.selectedConversationIds.length === 0;
      const groupsOnly = readAll && this.conversationFilter === 'groups';
      const ids = groupsOnly ? this.myGroups().map((g) => g.id) : [...this.selectedConversationIds];
      const markingEverything = readAll && !groupsOnly;

      // Nothing to mark (Groups tab without any joined groups) — an empty ids
      // list would read as "mark everything" on the server.
      if (!markingEverything && ids.length === 0) {
        this.exitSelectMode();
        return;
      }

      try {
        const response = await this.$api({
          url: '/chat/conversations/read',
          method: 'POST',
          data: markingEverything ? {} : { conversation_ids: ids }
        });

        this.conversations = this.conversations.map((conv) => (
          markingEverything || ids.includes(conv.id)
            ? { ...conv, unread_count: 0, marked_unread: false }
            : conv
        ));
        // Row clearing is best effort across the conversations we have loaded;
        // the count that comes back is authoritative either way.
        this.trackChat('inbox', 'mark read');
        this.syncNotificationBell(response, markingEverything ? this.conversations.map((c) => c.id) : ids);
        this.markReadAll = markingEverything;
        this.markReadCount = ids.length;
        // "Read all" completes the task, so leave select mode. A selection
        // stays checked so the action can switch to marking those chats unread.
        if (readAll) this.exitSelectMode();
        this.markReadSuccess = true;
        setTimeout(() => { this.markReadSuccess = false; }, 4000);
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            message: 'Failed to mark conversations as read',
            conversationIds: markingEverything ? 'all' : ids
          }
        });
      }
    },

    async markConversationRead(convId = null) {
      const conversationId = convId ?? this.activeConversationId;
      if (!conversationId) return;

      this.closeListMenu();
      this.closeDmActionsMenu();
      this.closeGroupActionsMenu();
      try {
        const response = await this.$api({
          url: `/chat/conversations/${conversationId}/memberships/read`,
          method: 'POST'
        });
        this.trackChat('inbox', 'mark read');
        this.applyReadLocally([conversationId]);
        this.syncNotificationBell(response, [conversationId]);
        this.loadHiddenUnreadCount();
      } catch (err) {
        console.error('Failed to mark conversation as read:', err);
      }
    },

    async markConversationUnread(convId = null) {
      const conversationId = convId ?? this.activeConversationId;
      if (!conversationId) return;

      this.closeListMenu();
      this.closeDmActionsMenu();
      this.closeGroupActionsMenu();
      await this.markConversationsUnread([conversationId]);
    },

    async markSelectedConversationsUnread() {
      const ids = [...this.selectedConversationIds];
      if (ids.length === 0) return;

      const marked = await this.markConversationsUnread(ids);
      if (!marked) return;

      this.markUnreadCount = ids.length;
      this.markUnreadSuccess = true;
      setTimeout(() => { this.markUnreadSuccess = false; }, 4000);
    },

    async markConversationsUnread(ids) {
      try {
        await this.$api({
          url: '/chat/conversations/unread',
          method: 'POST',
          data: { conversation_ids: ids }
        });
        this.trackChat('inbox', 'mark unread');
        const flag = (conv) => (ids.includes(conv.id) ? { ...conv, marked_unread: true } : conv);
        this.conversations = this.conversations.map(flag);
        this.hiddenConversations = this.hiddenConversations.map(flag);
        this.blockedConversations = this.blockedConversations.map(flag);
        return true;
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            message: 'Failed to mark conversations as unread',
            conversationIds: ids
          }
        });
        return false;
      }
    },

    applyReadLocally(ids) {
      const clear = (conv) => (
        ids.includes(conv.id) ? { ...conv, unread_count: 0, marked_unread: false } : conv
      );
      this.conversations = this.conversations.map(clear);
      this.hiddenConversations = this.hiddenConversations.map(clear);
      this.blockedConversations = this.blockedConversations.map(clear);
    },

    async hideSelectedConversations() {
      const ids = [...this.selectedConversationIds];
      if (ids.length === 0) return;

      const hiddenIds = [];
      try {
        // One at a time so a later failure still drops the chats that did hide.
        for (const id of ids) {
          await this.$api({
            url: `/chat/conversations/${id}/hide`,
            method: 'POST'
          });
          hiddenIds.push(id);
        }
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            message: 'Failed to hide selected conversations',
            conversationIds: ids
          }
        });
      }

      if (hiddenIds.length === 0) return;
      this.trackChat('inbox', 'hide');

      const hidden = [];
      this.conversations = this.conversations.filter((conv) => {
        if (!hiddenIds.includes(conv.id)) return true;
        conv.is_hidden = true;
        hidden.push(conv);
        return false;
      });
      hidden.forEach((conv) => {
        if (!this.hiddenConversations.some((c) => c.id === conv.id)) {
          this.hiddenConversations = [conv, ...this.hiddenConversations];
        }
      });
      this.selectedConversationIds = this.selectedConversationIds.filter((id) => !hiddenIds.includes(id));
      this.lastHiddenConversationId = hiddenIds[hiddenIds.length - 1];
      this.lastHiddenConversationIds = hiddenIds;
      this.loadHiddenUnreadCount();
      this.hideConversationSuccess = true;
      clearTimeout(this._hideConversationSuccessTimer);
      this._hideConversationSuccessTimer = setTimeout(() => { this.hideConversationSuccess = false; }, 4000);
    },

    markReadToastMessage() {
      const key = 'chat.conversation_list.marked_read';
      return window.i18n?.t(key, { count: this.markReadCount }) || key;
    },

    markUnreadToastMessage() {
      const key = 'chat.conversation_list.marked_unread';
      return window.i18n?.t(key, { count: this.markUnreadCount }) || key;
    }
  };
}